import { useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { createAlert } from '@/api/listing-alerts'
import { pick } from 'lodash'

import { isGroup } from '@/utils/listings'
import useSessionComplete from '@/hooks/use-session-complete'

export const toCountry = (state: string) => {
  const stateToCountryMap: Record<string, string> = {
    AB: 'CA',
    BC: 'CA',
    MB: 'CA',
    NB: 'CA',
    NL: 'CA',
    NS: 'CA',
    NT: 'CA',
    NU: 'CA',
    ON: 'CA',
    PE: 'CA',
    QC: 'CA',
    SK: 'CA',
    YT: 'CA',
    AL: 'US',
    AK: 'US',
    AZ: 'US',
    AR: 'US',
    CA: 'US',
    CO: 'US',
    CT: 'US',
    DE: 'US',
    FL: 'US',
    GA: 'US',
    HI: 'US',
    ID: 'US',
    IL: 'US',
    IN: 'US',
    IA: 'US',
    KS: 'US',
    KY: 'US',
    LA: 'US',
    ME: 'US',
    MD: 'US',
    MA: 'US',
    MI: 'US',
    MN: 'US',
    MS: 'US',
    MO: 'US',
    MT: 'US',
    NE: 'US',
    NV: 'US',
    NH: 'US',
    NJ: 'US',
    NM: 'US',
    NY: 'US',
    NC: 'US',
    ND: 'US',
    OH: 'US',
    OK: 'US',
    OR: 'US',
    PA: 'US',
    RI: 'US',
    SC: 'US',
    SD: 'US',
    TN: 'US',
    TX: 'US',
    UT: 'US',
    VT: 'US',
    VA: 'US',
    WA: 'US',
    WV: 'US',
    WI: 'US',
    WY: 'US'
  }

  return (
    stateToCountryMap[state] ||
    (typeof window !== 'undefined'
      ? window.navigator.language.split('-')[1]
      : 'Unknown')
  )
}

export const useAlerts = () => {
  const { t } = useTranslation()
  const isLoggedIn = useSessionComplete()

  const sendAlert = useCallback(
    async (listing: any) => {
      let _listing

      if (listing.listings?.[0]) {
        _listing = {
          city: listing?.city,
          bedrooms: listing.listings?.[0]?.bedrooms,
          bathrooms: listing.listings?.[0]?.bathrooms,
          gr_unit: listing.listings?.[0]?.gr_unit,
          gr_min_price: listing.listings?.[0]?.gr_min_price,
          gr_max_price: listing.listings?.[0]?.gr_max_price,
          price: listing.listings?.[0]?.price,
          state: listing?.state,
          country: toCountry(listing?.state)
        }
      } else {
        _listing = {
          ...pick(listing, [
            'city',
            'bedrooms',
            'bathrooms',
            'gr_unit',
            'gr_min_price',
            'gr_max_price',
            'price',
            'state'
          ]),
          country: toCountry(listing?.state)
        }
      }

      const name = `${_listing?.city} ${t(
        'b.listings.view.rentals.text',
        'Rentals'
      )}`

      const output = await createAlert({
        name,
        criteria: {
          cities: [_listing?.city],
          bedroom_count: [String(_listing?.bedrooms)],
          bathroom_count: [String(_listing?.bathrooms)],
          min_price: Number(
            isGroup(_listing) ? _listing?.gr_min_price : _listing?.price
          ),
          max_price: Number(
            isGroup(_listing) ? _listing?.gr_max_price : _listing?.price
          ),
          state: _listing?.state,
          country: toCountry(_listing?.state) // TODO probably better way
        },
        isLoggedIn: true
      })

      return output
    },
    [t]
  )

  // only logged in users can send alerts
  const sendAlertIfLoggedIn = useCallback(
    (listing: any) => {
      if (isLoggedIn) {
        sendAlert(listing)
      }
    },
    [isLoggedIn, sendAlert]
  )

  return {
    sendAlert,
    sendAlertIfLoggedIn
  }
}
